import React, { useEffect, useState } from 'react'
import {
  Dialog,
  Heading,
  Divider,
  Content,
  Text,
  TableView,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
  ProgressCircle,
  View,
  StatusLight,
  Badge,
  Button
} from '@adobe/react-spectrum'
import { connect } from 'react-redux'
import { getJobDetails } from '../../redux/actions/homeActions'
import { CheckCircle, XCircle, Clock, Calendar, Cpu, ExternalLink, Info } from 'lucide-react'

interface JobPreviewDialogProps {
  jobId: string
  isOpen: boolean
  onClose: () => void
  translate?: (key: string) => string
  jobDetails?: any
  jobDetailsLoading?: boolean
  getJobDetails?: (id: string) => void
}

type TestResultCategory = 'pass' | 'fail' | 'info' | 'pending'

const getTestResultCategory = (result?: string): TestResultCategory => {
  if (!result) return 'pending'
  switch (result.toLowerCase()) {
    case 'pass':
    case 'passed':
    case 'marginalpass':
      return 'pass'
    case 'fail':
    case 'failed':
    case 'marginalfail':
    case 'aborted':
      return 'fail'
    case 'info':
      return 'info'
    case 'pending':
    case 'null':
    default:
      return 'pending'
  }
}

const isInfoOnlyResult = (result?: string): boolean => {
  if (!result) return true
  const normalized = result.trim().toLowerCase()
  return normalized === '' || normalized === 'null' || normalized === 'n/a'
}

const resultCategoryStyles: Record<TestResultCategory, { bg: string; color: string; icon: 'check' | 'x' | 'clock' }> = {
  pass:    { bg: '#f0fdf4', color: '#166534', icon: 'check' },
  fail:    { bg: '#fef2f2', color: '#991b1b', icon: 'x' },
  info:    { bg: '#eff6ff', color: '#1e40af', icon: 'clock' },
  pending: { bg: '#f5f5f5', color: '#525252', icon: 'clock' }
}

const JobPreviewDialog: React.FC<JobPreviewDialogProps> = ({
  jobId,
  isOpen,
  onClose,
  translate: translateProp,
  jobDetails,
  jobDetailsLoading,
  getJobDetails
}) => {
  const t = (key: string) => translateProp ? translateProp(key) : key
  useEffect(() => {
    if (isOpen && jobId && getJobDetails) {
      getJobDetails(jobId)
    }
  }, [isOpen, jobId, getJobDetails])

  const getLocale = () => window.localStorage.getItem('language') || 'en-GB'

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return t('not_available')
    try {
      const locale = getLocale()
      const date = new Date(dateString)
      const dateFormatted = date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      const timeFormatted = date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      return `${dateFormatted} ${timeFormatted}`
    } catch {
      return dateString
    }
  }

  const calculateSuccessRate = (passed: number, total: number): number => {
    if (total === 0) return 0
    return parseFloat(((passed / total) * 100).toFixed(1))
  }

  const handleOpenJob = () => {
    window.location.href = `/job/${jobId}`
  }

  if (!isOpen) return null

  const job = jobDetails?.data
  const successRate = job ? calculateSuccessRate(job.passedTests || 0, job.totalTests || 0) : 0
  const translatedInfoLabel = t('information')
  const infoLabel = translatedInfoLabel === 'information' ? 'Information' : translatedInfoLabel

  return (
    <Dialog isDismissable UNSAFE_style={{ width: '800px', maxHeight: '90vh' }}>
      <Heading UNSAFE_style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>{job?.name || t('job_details_heading')}</span>
          {job && (job.status === 'completed' || job.status === 'pending') && (
            <Badge variant={job.status === 'completed' ? 'positive' : 'yellow'}>
              {job.status === 'completed' ? t('status_completed') : t('status_pending')}
            </Badge>
          )}
        </div>
        <Button variant="secondary" onPress={handleOpenJob} UNSAFE_style={{ marginLeft: 'auto' }}>
          <ExternalLink size={14} style={{ marginRight: '6px' }} />
          {t('open_job')}
        </Button>
      </Heading>
      <Divider />
      <Content UNSAFE_style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 150px)', padding: '20px 0' }}>
        {jobDetailsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px' }}>
            <ProgressCircle isIndeterminate size="L" />
          </div>
        ) : job ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Job Info Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              padding: '0'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Cpu size={16} style={{ color: '#0284c7' }} />
                  <Text UNSAFE_style={{ fontSize: '11px', color: '#0284c7', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('device_label')}</Text>
                </div>
                <Text UNSAFE_style={{ fontSize: '14px', fontWeight: 600, color: '#0c4a6e' }}>
                  {job.deviceType ? t(job.deviceType) : t('lantekIII')}
                </Text>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #c4b5fd'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Calendar size={16} style={{ color: '#7c3aed' }} />
                  <Text UNSAFE_style={{ fontSize: '11px', color: '#7c3aed', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('created_label')}</Text>
                </div>
                <Text UNSAFE_style={{ fontSize: '13px', fontWeight: 500, color: '#4c1d95' }}>
                  {formatDateTime(job.createdDate)}
                </Text>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #6ee7b7'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Clock size={16} style={{ color: '#059669' }} />
                  <Text UNSAFE_style={{ fontSize: '11px', color: '#059669', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('completed_label')}</Text>
                </div>
                <Text UNSAFE_style={{ fontSize: '13px', fontWeight: 500, color: '#064e3b' }}>
                  {formatDateTime(job.completedDate)}
                </Text>
              </div>
            </div>

            {/* Test Summary with Progress */}
            <div style={{
              background: '#fafafa',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e5e5e5'
            }}>
              <Text UNSAFE_style={{ fontSize: '13px', color: '#525252', fontWeight: 600, marginBottom: '16px', display: 'block' }}>
                {t('test_summary')}
              </Text>

              {/* Progress Bar */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Text UNSAFE_style={{ fontSize: '12px', color: '#737373' }}>{t('success_rate')}</Text>
                  <Text UNSAFE_style={{ fontSize: '14px', fontWeight: 'bold', color: successRate >= 80 ? '#22c55e' : successRate >= 50 ? '#f59e0b' : '#ef4444' }}>
                    {successRate.toFixed(1)}%
                  </Text>
                </div>
                <div style={{
                  height: '8px',
                  background: '#e5e5e5',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  display: 'flex'
                }}>
                  <div style={{
                    width: `${(job.passedTests / (job.totalTests || 1)) * 100}%`,
                    background: '#22c55e',
                    transition: 'width 0.3s ease'
                  }} />
                  <div style={{
                    width: `${(job.failedTests / (job.totalTests || 1)) * 100}%`,
                    background: '#ef4444',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* Stats Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div style={{
                  background: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                  <Text UNSAFE_style={{ fontSize: '11px', color: '#737373', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>{t('totaltest')}</Text>
                  <Text UNSAFE_style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '4px', color: '#171717', display: 'block' }}>
                    {job.totalTests || 0}
                  </Text>
                </div>
                <div style={{
                  background: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  borderLeft: '3px solid #22c55e'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    <CheckCircle size={12} style={{ color: '#22c55e' }} />
                    <Text UNSAFE_style={{ fontSize: '11px', color: '#22c55e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('passed_label')}</Text>
                  </div>
                  <Text UNSAFE_style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '4px', color: '#22c55e' }}>
                    {job.passedTests || 0}
                  </Text>
                </div>
                <div style={{
                  background: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  borderLeft: '3px solid #ef4444'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    <XCircle size={12} style={{ color: '#ef4444' }} />
                    <Text UNSAFE_style={{ fontSize: '11px', color: '#ef4444', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('failed_label')}</Text>
                  </div>
                  <Text UNSAFE_style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '4px', color: '#ef4444' }}>
                    {job.failedTests || 0}
                  </Text>
                </div>
              </div>
            </div>

            {/* Tests Table */}
            {job.tests && job.tests.length > 0 && (
              <div>
                <Text UNSAFE_style={{ fontSize: '13px', color: '#525252', fontWeight: 600, marginBottom: '12px', display: 'block' }}>
                  {t('individual_tests')} ({job.tests.length})
                </Text>
                <div style={{
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <TableView
                    aria-label="Tests table"
                    UNSAFE_style={{ width: '100%' }}
                    density="compact"
                    height="300px"
                  >
                    <TableHeader>
                      <Column key="name">{t('test_name_header')}</Column>
                      <Column key="limits" width={240}>{t('limits_header')}</Column>
                      <Column key="timestamp" width={150}>{t('date_time_header')}</Column>
                      <Column key="result" width={100}>{t('result_header')}</Column>
                    </TableHeader>
                    <TableBody>
                      {job.tests.map((test: any, index: number) => (
                        <Row key={test.id}>
                          <Cell>
                            <Text UNSAFE_style={{ fontSize: '13px', fontWeight: 500 }}>{test.name}</Text>
                          </Cell>
                          <Cell>
                            <Text UNSAFE_style={{ fontSize: '12px', color: '#737373' }}>
                              {test.limits || '-'}
                            </Text>
                          </Cell>
                          <Cell>
                            <Text UNSAFE_style={{ fontSize: '12px', color: '#737373' }}>
                              {formatDateTime(test.timestamp)}
                            </Text>
                          </Cell>
                          <Cell>
                            {(() => {
                              const category = getTestResultCategory(test.result)
                              const style = resultCategoryStyles[category]
                              const infoOnly = isInfoOnlyResult(test.result)
                              return (
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  background: style.bg
                                }}
                                aria-label={infoOnly ? infoLabel : test.result}
                                title={infoOnly ? infoLabel : test.result}
                                >
                                  {infoOnly ? (
                                    <Info size={12} style={{ color: style.color }} />
                                  ) : style.icon === 'check' ? (
                                    <CheckCircle size={12} style={{ color: style.color }} />
                                  ) : style.icon === 'x' ? (
                                    <XCircle size={12} style={{ color: style.color }} />
                                  ) : (
                                    <Clock size={12} style={{ color: style.color }} />
                                  )}
                                  {!infoOnly && (
                                    <Text UNSAFE_style={{
                                      fontSize: '12px',
                                      color: style.color,
                                      fontWeight: 600,
                                      textTransform: 'capitalize'
                                    }}>
                                      {test.result}
                                    </Text>
                                  )}
                                </div>
                              )
                            })()}
                          </Cell>
                        </Row>
                      ))}
                    </TableBody>
                  </TableView>
                </div>
              </div>
            )}

            {/* Notes/Description */}
            {(job.description || job.notes) && (
              <div style={{
                background: '#fafafa',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e5e5e5'
              }}>
                {job.description && (
                  <div style={{ marginBottom: job.notes ? '12px' : '0' }}>
                    <Text UNSAFE_style={{ fontSize: '11px', color: '#737373', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('description_label')}</Text>
                    <Text UNSAFE_style={{ marginTop: '6px', fontSize: '13px', whiteSpace: 'pre-wrap', color: '#404040', display: 'block' }}>
                      {job.description}
                    </Text>
                  </div>
                )}
                {job.notes && (
                  <div>
                    <Text UNSAFE_style={{ fontSize: '11px', color: '#737373', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('notes_label')}</Text>
                    <Text UNSAFE_style={{ marginTop: '6px', fontSize: '13px', whiteSpace: 'pre-wrap', color: '#404040', display: 'block' }}>
                      {job.notes}
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Text UNSAFE_style={{ color: '#737373' }}>{t('failed_load_job_details')}</Text>
          </div>
        )}
      </Content>
    </Dialog>
  )
}

const mapStateToProps = (state: any) => ({
  jobDetails: state.homeReducer?.jobDetails,
  jobDetailsLoading: state.homeReducer?.jobDetails?.loading
})

const mapDispatchToProps = (dispatch: any) => ({
  getJobDetails: (jobId: string) => dispatch(getJobDetails(jobId))
})

export default connect(mapStateToProps, mapDispatchToProps)(JobPreviewDialog)
